import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";

import { TicketingFormCategory } from "@simplyk/common";

import { TicketingRecurrenceOutput } from "@/common/types/trpc";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

interface RateContextValue {
  rateId: string;
  rateIndex: number;
  formCategory: TicketingFormCategory;
  isSweepstakes: boolean;
  isEditing: boolean;
  isOnboarding: boolean;
  isRegistration: boolean;
  recurrences: TicketingRecurrenceOutput[] | undefined;
  hasMultipleDates: boolean;
  alreadyBoughtTicketsCount: number;
  hasExistingPayments: boolean;
  canModifyValidityAtDate: boolean;
  unlockValidityAtDate: () => void;
  canModifyRenewalAmount: boolean;
  unlockRenewalAmount: () => void;
  isAddOn?: boolean;
}

const RateContext = createContext<RateContextValue | null>(null);

interface RateContextProviderProps {
  rateId: string;
  rateIndex: number;
  formCategory: TicketingFormCategory;
  isEditing: boolean;
  isOnboarding: boolean;
  isRegistration: boolean;
  recurrences: TicketingRecurrenceOutput[] | undefined;
  hasMultipleDates?: boolean;
  alreadyBoughtTicketsCount: number;
  isAddOn?: boolean;
}

export const RateContextProvider: FC<PropsWithChildren<RateContextProviderProps>> = ({
  children,
  rateId,
  rateIndex,
  formCategory,
  isEditing,
  isOnboarding,
  isRegistration,
  recurrences,
  hasMultipleDates = false,
  alreadyBoughtTicketsCount,
  isAddOn,
}) => {
  // Shared across the validity fields so confirming the rate-change dialog once
  // unlocks both the renewal-mode radio and the day/month pickers.
  const [canModifyValidityAtDate, setCanModifyValidityAtDate] = useState(alreadyBoughtTicketsCount === 0);
  const unlockValidityAtDate = useCallback(() => setCanModifyValidityAtDate(true), []);
  const [canModifyRenewalAmount, setCanModifyRenewalAmount] = useState(alreadyBoughtTicketsCount === 0);
  const unlockRenewalAmount = useCallback(() => setCanModifyRenewalAmount(true), []);

  const value = useMemo<RateContextValue>(
    () => ({
      rateId,
      rateIndex,
      formCategory,
      isSweepstakes: isSweepstakesCategory(formCategory),
      isEditing,
      isOnboarding,
      isRegistration,
      recurrences,
      hasMultipleDates,
      alreadyBoughtTicketsCount,
      hasExistingPayments: alreadyBoughtTicketsCount > 0,
      canModifyValidityAtDate,
      unlockValidityAtDate,
      canModifyRenewalAmount,
      unlockRenewalAmount,
      isAddOn,
    }),
    [
      rateId,
      rateIndex,
      formCategory,
      isEditing,
      isOnboarding,
      isRegistration,
      recurrences,
      hasMultipleDates,
      alreadyBoughtTicketsCount,
      canModifyValidityAtDate,
      unlockValidityAtDate,
      canModifyRenewalAmount,
      unlockRenewalAmount,
      isAddOn,
    ]
  );

  return <RateContext.Provider value={value}>{children}</RateContext.Provider>;
};

export const useRateContext = (): RateContextValue => {
  const ctx = useContext(RateContext);
  if (!ctx) {
    throw new Error("useRateContext must be used within a RateContextProvider");
  }
  return ctx;
};
